import { EventListItemSkeleton, GenericTextSkeleton } from "@hooplab/ui-components";
import { format, getYear } from "date-fns";
import { ReactComponent as IconSearch } from "../../assets/icons/search.svg";
import { ReactComponent as IconCross } from "../../assets/icons/icon_cross.svg";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import BackendRequest from "../../components/BackendRequest";
import EventListItemView from "../../components/EventListItem";
import Heading from "../../components/Heading";
import { Box } from "@hooplab/ui-components";
import { fetchEvents } from "../../dao/events";
import colors from "../../styles/colors";
import { EventListItem } from "../../types";
import ErrorPage from "../ErrorPage";
import * as styles from "./styles";
import { getLocale } from "../../helpers/prettyDate";
import { useQueryParamsLowercase } from "../../hooks/useQueryParams";
import { useNavigate } from "react-router-dom";
import { useCurrentLangContext } from "../../hooks/useCurrentLang";

const getEventGroupKey = (d: Date) => {
  const now = new Date();
  if (now > d) {
    return "Ongoing";
  } else {
    return `${getYear(d)}-${format(d, "MM")}`;
  }
};

const groupEvents = (events: EventListItem[]) =>
  events.reduce((acc, event) => {
    const key = getEventGroupKey(event.start);
    if (acc[key]) {
      acc[key].push(event);
    } else {
      acc[key] = [event];
    }

    return acc;
  }, {} as { [k: string]: EventListItem[] });

const getFilteredEvents = (
  events: EventListItem[],
  query: string | null,
  showHiddenEvents: boolean,
) => {
  if (!query || events.length < 3) {
    return events;
  }
  const q = query.toLowerCase();
  if (showHiddenEvents) {
    return events.filter((e) => e.title.toLowerCase().indexOf(q) >= 0);
  }
  return events.filter((e) => e.title.toLowerCase().indexOf(q) >= 0 && e.saleState === "active");
};

export const EventListSkeleton = () => (
  <div className={styles.container}>
    <div className={styles.list}>
      <div className={styles.listHeading}>
        <GenericTextSkeleton fontSize="1.25rem" />
      </div>
      {Array.apply(null, Array(5)).map((v, i) => (
        <div key={i} className={styles.listItem}>
          <EventListItemSkeleton />
        </div>
      ))}
    </div>
  </div>
);

const EventList: React.FC<{ organizationName: string }> = (props) => {
  const [query, setQuery] = React.useState<string | null>(null);
  const [showHiddenEvents] = React.useState(false);
  const nowKey = getEventGroupKey(new Date());
  const {
    provided: {
      currentLocale: { name: lang },
    },
  } = useCurrentLangContext()!;

  const params = useQueryParamsLowercase();

  const navigate = useNavigate();

  const addParams = (param: string, value: string) => {
    if (!value || value === "") {
      params.delete(param);
    } else {
      params.delete(param);
      params.append(param, value);
    }
    navigate({ pathname: "", search: params.toString() }, { replace: true });
  };

  React.useEffect(() => {
    if (params.has("q")) {
      setQuery(params.get("q"));
    } else {
      setQuery(null);
    }
  }, [params]);

  const intl = useIntl();

  const renderGroupHeader = (key: string, event: EventListItem) => {
    if (key === nowKey) {
      return (
        <FormattedMessage
          id={"screens.OrganizationEventList.EventList.thisMonth"}
          defaultMessage={"This month"}
        />
      );
    } else if (key === "Ongoing") {
      return (
        <FormattedMessage
          id={"screens.OrganizationEventList.EventList.ongoing"}
          defaultMessage={"Ongoing"}
        />
      );
    }
    const formatted = format(event.start, "MMMM yyyy", { locale: getLocale(lang) });
    return (
      <React.Fragment>{formatted.charAt(0).toUpperCase() + formatted.slice(1)}</React.Fragment>
    );
  };

  return (
    <BackendRequest requestFn={fetchEvents}>
      {{
        loading: () => <EventListSkeleton />,
        error: (error) => <ErrorPage />,
        success: (events) => {
          const eventGroups = groupEvents(getFilteredEvents(events, query, showHiddenEvents));

          if (events.length === 0) {
            return (
              <div className={styles.noEventsContainer}>
                <FormattedMessage
                  id={"screens.OrganizationEventList.EventList.noFutureEvents"}
                  defaultMessage={"No future events"}
                />
              </div>
            );
          }

          return (
            <div className={styles.container}>
              {events.length >= 3 && (
                <Box additionalCls={styles.inputWrapper}>
                  <IconSearch width={18} height={18} color="#B6B6B6" className={styles.inputIcon} />
                  <input
                    className={styles.input}
                    value={query || ""}
                    onChange={(e) => addParams("q", e.target.value)}
                    placeholder={intl.formatMessage(
                      {
                        id: "screens.OrganizationEventList.EventList.findSomething",
                        defaultMessage: "Find something happening at {name}",
                      },
                      {
                        name: props.organizationName,
                      },
                    )}
                  />
                  {query && query !== "" && (
                    <button onClick={() => addParams("q", "")} className={styles.crossIcon}>
                      <IconCross height="14" />
                    </button>
                  )}
                </Box>
              )}
              {query &&
                query.length > 0 &&
                events.length >= 3 &&
                (Object.keys(eventGroups).length === 0 ? (
                  <div className={styles.noEventsContainer}>
                    <FormattedMessage
                      id={"screens.OrganizationEventList.EventList.noEventsFound"}
                      defaultMessage={
                        "We could not find any events with the search query '{query}'"
                      }
                      values={{ query: query }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-row text-sm md:max-w-[600px] mx-[1.625rem] md:mx-auto mt-4 justify-center">
                    <span className="[&>span]:mr-2">
                      <FormattedMessage
                        tagName={"span"}
                        id={"screens.OrganizationEventList.EventList.showingResultsFor"}
                        defaultMessage={'Showing results for "{query}"'}
                        values={{ query: query }}
                      />
                      <button
                        onClick={() => addParams("q", "")}
                        className="underline hover:opacity-90 text-gray-700 inline-flex"
                      >
                        <FormattedMessage
                          id="screens.OrganizationEventList.EventList.remove"
                          defaultMessage={"Remove"}
                        />
                      </button>
                    </span>
                  </div>
                ))}

              {Object.keys(eventGroups)
                .sort((a, b) => {
                  if (a === "Ongoing") {
                    return -1;
                  } else if (b === "Ongoing") {
                    return 1;
                  } else {
                    return a.localeCompare(b);
                  }
                })
                .map((yearAndMonth) => {
                  const events = eventGroups[yearAndMonth];

                  return (
                    <div className={styles.list} key={yearAndMonth}>
                      <div className={styles.listHeading}>
                        <Heading color={colors.grey[500]} size="xl">
                          {renderGroupHeader(yearAndMonth, events[0])}
                        </Heading>
                      </div>
                      {events.map((event) => (
                        <div key={event.eventId} className={styles.listItem}>
                          <EventListItemView {...event} />
                        </div>
                      ))}
                    </div>
                  );
                })}
            </div>
          );
        },
      }}
    </BackendRequest>
  );
};

export default EventList;
